/* eslint-disable fp/no-mutation */
import React, { useRef, useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import axios from 'src/utilities/http-client'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  Image as ImageIcon,
} from '@mui/icons-material'
import { fetchLinkedServices } from 'src/v3/manage/manage.slice'
import { useAppDispatch } from 'src/store/hooks'
import { TextElement, ImageElement } from './types'
import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  PROTECTED_ZONES,
  FONT_SIZES,
} from './constants'
import DraggableText from './draggable-text'
import DraggableImage from './draggable-image'

type TextCustomizationStepProps = {
  flattenedBackground: string | null
  textElements: TextElement[]
  selectedTextId: string | null
  imageElements: ImageElement[]
  selectedImageId: string | null
  onAddText: (text: TextElement) => void
  onUpdateText: (id: string, updates: Partial<TextElement>) => void
  onDeleteText: (id: string) => void
  onSelectText: (id: string | null) => void
  onAddImage: (image: ImageElement) => void
  onUpdateImage: (id: string, updates: Partial<ImageElement>) => void
  onDeleteImage: (id: string) => void
  onSelectImage: (id: string | null) => void
  onComplete: (blob: Blob) => void
  linkedServiceId?: string
}

export default ({
  flattenedBackground,
  textElements,
  selectedTextId,
  imageElements,
  selectedImageId,
  onAddText,
  onUpdateText,
  onDeleteText,
  onSelectText,
  onAddImage,
  onUpdateImage,
  onDeleteImage,
  onSelectImage,
  onComplete,
  linkedServiceId,
}: TextCustomizationStepProps) => {
  let dispatch = useAppDispatch()
  let containerRef = useRef<HTMLDivElement>(null)
  let canvasRef = useRef<HTMLCanvasElement>(null)
  let [scale, setScale] = useState(0.5)
  let [saving, setSaving] = useState(false)

  let selectedText = textElements.find((el) => el.id === selectedTextId)
  let selectedImage = imageElements.find((el) => el.id === selectedImageId)

  let addNewText = () => {
    let newText: TextElement = {
      id: Date.now().toString(),
      content: 'New Text',
      x: 200,
      y: 600,
      fontSize: 24,
      bold: false,
      italic: false,
      underline: false,
    }
    onAddText(newText)
  }

  let addNewImage = (file: File) => {
    let reader = new FileReader()
    reader.onload = (e) => {
      let img = new Image()
      img.onload = () => {
        let maxSize = 200
        let aspectRatio = img.width / img.height
        let width = aspectRatio >= 1 ? maxSize : maxSize * aspectRatio
        let height = aspectRatio >= 1 ? maxSize / aspectRatio : maxSize

        let newImage: ImageElement = {
          id: Date.now().toString(),
          file,
          dataUrl: e.target?.result as string,
          x: 100,
          y: 100,
          width,
          height,
          originalWidth: img.width,
          originalHeight: img.height,
        }
        onAddImage(newImage)
      }
      img.src = e.target?.result as string
    }
    reader.readAsDataURL(file)
  }

  let handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      addNewImage(file)
    }
    // Reset input
    e.target.value = ''
  }

  let saveCertificateDesign = async () => {
    if (!linkedServiceId) {
      // eslint-disable-next-line no-console
      console.error('No linked service ID provided')
      return
    }

    setSaving(true)

    try {
      let canvas = canvasRef.current
      if (!canvas || !flattenedBackground) return

      let ctx = canvas.getContext('2d')
      if (!ctx) return

      canvas.width = CANVAS_WIDTH
      canvas.height = CANVAS_HEIGHT

      let img = new Image()
      await new Promise<void>((resolve) => {
        img.onload = async () => {
          // Draw the flattened background (includes user image + template)
          ctx.drawImage(img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

          // Load and draw overlay images
          let imagePromises = imageElements.map(
            (imageEl) =>
              new Promise<void>((res) => {
                let overlayImg = new Image()
                overlayImg.onload = () => {
                  ctx.drawImage(
                    overlayImg,
                    imageEl.x,
                    imageEl.y,
                    imageEl.width,
                    imageEl.height,
                  )
                  res()
                }
                overlayImg.src = imageEl.dataUrl
              }),
          )

          await Promise.all(imagePromises)

          // Draw user's custom text elements on top
          textElements.forEach((textEl) => {
            ctx.fillStyle = 'black'
            ctx.textAlign = 'left'
            ctx.textBaseline = 'top'

            let fontStyle = ''
            if (textEl.italic) fontStyle += 'italic '
            if (textEl.bold) fontStyle += 'bold '
            ctx.font = `${fontStyle}${textEl.fontSize}px Arial`

            if (textEl.underline) {
              ctx.fillText(textEl.content, textEl.x, textEl.y)
              let metrics = ctx.measureText(textEl.content)
              ctx.fillRect(
                textEl.x,
                textEl.y + textEl.fontSize + 2,
                metrics.width,
                2,
              )
            } else {
              ctx.fillText(textEl.content, textEl.x, textEl.y)
            }
          })

          resolve()
        }
        img.src = flattenedBackground
      })

      // Convert canvas to blob
      let blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blb) => {
          if (blb) resolve(blb)
        }, 'image/png')
      })

      if (!blob) return

      // Get presigned URL
      let response = await axios.put(
        `/api/manage/certificate/${linkedServiceId}/design`,
      )
      let presignedUrl = response.data

      // Upload to S3
      await axios.put(presignedUrl, blob, {
        headers: {
          'Cache-Control': 'max-age=2592000',
          'content-type': 'image/png',
        },
      })

      // Wait for 10 seconds before completing (simulate delay)
      await new Promise((res) => {
        setTimeout(res, 10000)
      })

      // Call onComplete to close the modal
      onComplete(blob)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving certificate design:', error)
    } finally {
      setSaving(false)
      dispatch(fetchLinkedServices())
    }
  }

  let generateFinalImage = async () => {
    let canvas = canvasRef.current
    if (!canvas || !flattenedBackground) return

    let ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = CANVAS_WIDTH
    canvas.height = CANVAS_HEIGHT

    let img = new Image()
    img.onload = async () => {
      // Draw the flattened background (includes user image + template)
      ctx.drawImage(img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

      // Load and draw overlay images
      let imagePromises = imageElements.map(
        (imageEl) =>
          new Promise<void>((resolve) => {
            let overlayImg = new Image()
            overlayImg.onload = () => {
              ctx.drawImage(
                overlayImg,
                imageEl.x,
                imageEl.y,
                imageEl.width,
                imageEl.height,
              )
              resolve()
            }
            overlayImg.src = imageEl.dataUrl
          }),
      )

      await Promise.all(imagePromises)

      // Draw user's custom text elements on top
      textElements.forEach((textEl) => {
        ctx.fillStyle = 'black'
        ctx.textAlign = 'left'
        ctx.textBaseline = 'top'

        let fontStyle = ''
        if (textEl.italic) fontStyle += 'italic '
        if (textEl.bold) fontStyle += 'bold '
        ctx.font = `${fontStyle}${textEl.fontSize}px Arial`

        if (textEl.underline) {
          ctx.fillText(textEl.content, textEl.x, textEl.y)
          let metrics = ctx.measureText(textEl.content)
          ctx.fillRect(
            textEl.x,
            textEl.y + textEl.fontSize + 2,
            metrics.width,
            2,
          )
        } else {
          ctx.fillText(textEl.content, textEl.x, textEl.y)
        }
      })

      canvas.toBlob((blob) => {
        if (blob) onComplete(blob)
      }, 'image/png')
    }
    img.src = flattenedBackground
  }

  useEffect(() => {
    if (containerRef.current) {
      let containerWidth = containerRef.current.clientWidth - 50 // Less padding for text controls
      let newScale = Math.min(containerWidth / CANVAS_WIDTH, 0.7)
      setScale(newScale)
    }
  }, [])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[70vh]">
      <div className="lg:col-span-2" ref={containerRef}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Design your certificate
        </Typography>

        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            width: CANVAS_WIDTH * scale,
            height: CANVAS_HEIGHT * scale,
            maxWidth: '100%',
            mx: 'auto',
          }}
          onClick={(e) => {
            // Only deselect if clicking directly on the canvas, not on text/image elements
            if (e.target === e.currentTarget) {
              onSelectText(null)
              onSelectImage(null)
            }
          }}
        >
          {flattenedBackground && (
            <Box
              component="img"
              src={flattenedBackground}
              alt="Background"
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}

          {/* Protected Zones */}
          {PROTECTED_ZONES.map((zone, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                bgcolor: 'rgba(0, 0, 0, 0.8)',
                border: 1,
                borderStyle: 'solid',
                borderColor: 'error.main',
                pointerEvents: 'none',
                left: zone.x * scale,
                top: zone.y * scale,
                width: zone.width * scale,
                height: zone.height * scale,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* SVG X pattern */}
              <svg
                width="100%"
                height="100%"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  pointerEvents: 'none',
                }}
              >
                <line
                  x1="0"
                  y1="0"
                  x2="100%"
                  y2="100%"
                  stroke="red"
                  strokeWidth="1"
                />
                <line
                  x1="100%"
                  y1="0"
                  x2="0"
                  y2="100%"
                  stroke="red"
                  strokeWidth="1"
                />
              </svg>

              {/* Zone label */}
              <Box
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.9)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.5,
                  border: 1,
                  borderColor: 'error.main',
                  position: 'relative',
                }}
              >
                <Typography
                  color="error.main"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: Math.max(10 * scale, 8),
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    position: 'relative',
                  }}
                >
                  {zone.label}
                </Typography>
              </Box>
            </Box>
          ))}

          {imageElements.map((imageEl) => (
            <DraggableImage
              key={imageEl.id}
              element={imageEl}
              scale={scale}
              isSelected={selectedImageId === imageEl.id}
              onUpdate={(updates) => onUpdateImage(imageEl.id, updates)}
              onSelect={() => onSelectImage(imageEl.id)}
            />
          ))}

          {textElements.map((textEl) => (
            <DraggableText
              key={textEl.id}
              element={textEl}
              scale={scale}
              isSelected={selectedTextId === textEl.id}
              onUpdate={(updates) => onUpdateText(textEl.id, updates)}
              onSelect={() => onSelectText(textEl.id)}
            />
          ))}
        </Box>
      </div>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="h6">Controls</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={addNewText}
              sx={{ textTransform: 'none' }}
            >
              Add Text
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ImageIcon />}
              component="label"
              sx={{ textTransform: 'none' }}
            >
              Add Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click on text or image elements in the preview to edit them, or add
          new content.
        </Typography>

        {selectedText ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Edit Selected Text
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              label="Content"
              value={selectedText.content}
              onChange={(e) =>
                onUpdateText(selectedText.id, { content: e.target.value })
              }
              sx={{ bgcolor: 'background.paperDark' }}
            />

            <FormControl fullWidth>
              <InputLabel>Font Size</InputLabel>
              <Select
                value={selectedText.fontSize}
                label="Font Size"
                onChange={(e) =>
                  onUpdateText(selectedText.id, {
                    fontSize: Number(e.target.value),
                  })
                }
                sx={{ bgcolor: 'background.paperDark' }}
              >
                {FONT_SIZES.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}px
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Formatting
              </Typography>
              <ToggleButtonGroup
                value={[
                  selectedText.bold && 'bold',
                  selectedText.italic && 'italic',
                  selectedText.underline && 'underline',
                ].filter(Boolean)}
                onChange={(_, newFormats) => {
                  onUpdateText(selectedText.id, {
                    bold: newFormats.includes('bold'),
                    italic: newFormats.includes('italic'),
                    underline: newFormats.includes('underline'),
                  })
                }}
                size="small"
              >
                <ToggleButton value="bold" aria-label="bold">
                  <FormatBoldIcon />
                </ToggleButton>
                <ToggleButton value="italic" aria-label="italic">
                  <FormatItalicIcon />
                </ToggleButton>
                <ToggleButton value="underline" aria-label="underline">
                  <FormatUnderlinedIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                onDeleteText(selectedText.id)
                onSelectText(null)
              }}
              sx={{ textTransform: 'none' }}
            >
              Delete Text
            </Button>
          </Box>
        ) : selectedImage ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Edit Selected Image
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Drag to move, use the resize handle to scale while maintaining
              aspect ratio.
            </Typography>

            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                onDeleteImage(selectedImage.id)
                onSelectImage(null)
              }}
              sx={{ textTransform: 'none' }}
            >
              Delete Image
            </Button>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No element selected
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click on a text or image element in the preview to edit it, or use
              the buttons above to add new content.
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 'auto', pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={
              linkedServiceId ? saveCertificateDesign : generateFinalImage
            }
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving
              ? 'Saving...'
              : linkedServiceId
                ? 'Save'
                : 'Generate Certificate'}
          </Button>
        </Box>
      </Box>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  )
}
